/** Colony-sim tunables (sim-seconds). Seeds — tune by playing/sim. */
export const SIM = {
  /** Length of one in-game day, in sim-seconds. */
  DAY_SEC: 120,
  /** Fraction of the day that is night (colonists prefer to sleep). */
  NIGHT_FROM: 0.78,
  NIGHT_TO: 0.24,

  // movement
  WALK_SPEED: 0.42, // floor-fractions per sec (≈2.4s to cross a floor)
  ELEVATOR_SPEED: 1.6, // floors per sec while riding

  // needs (per sim-second)
  DRAIN: { rest: 0.7, food: 0.5, joy: 0.42 } as Record<string, number>,
  RESTORE: { rest: 3.2, food: 16, joy: 5 } as Record<string, number>,
  /** Below this a colonist will break off to satisfy the need. */
  SEEK_THRESHOLD: { rest: 32, food: 34, joy: 28 } as Record<string, number>,
  /** Critically low — heavy mood hit. */
  CRITICAL: 14,

  // mood
  MOOD_BASE: 52,
  BREAK_THRESHOLD: 22, // mood below this → a break (wander/refuse)
  BREAK_SEC: 14,

  // skills
  SKILL_MAX: 20,
  XP_PER_SEC: 0.6, // xp while working the matching job
  XP_PER_LEVEL: 60, // xp to advance a level (×(level+1))

  // production: rate multiplier from skill (level 0 → 0.5×, level 20 → 2.0×)
  skillMult(level: number): number {
    return 0.5 + (level / 20) * 1.5;
  },
  tierMult(tier: number): number {
    return 1 + (tier - 1) * 0.6;
  },

  // heat
  HEAT_DECAY: 0.35, // passive cool/sec
  HEAT_LAYLOW: 5.5, // extra cool/sec while laying low
  SAFE_THRESHOLD: 38,

  // raids
  RAID_ROLL_SEC: 6,
  BASE_RAID_RATE: 0.45, // peak per-roll chance at 100 heat
  RAID_TELEGRAPH_SEC: 12, // long enough to read the raid, pick a room to brace, and react
  // raid strength scales with heat and colony wealth (rooms + cash)
  RAID_BASE_STRENGTH: 8,
  // consequence severities by margin (raidStrength vs defense)
  SEIZE_PRODUCT_MAX: 0.7,
  SEIZE_CASH_MAX: 0.3,
  ARREST_RECOVER_SEC: 90, // jailed colonist returns after this (if not "gone")
  INJURE_RECOVER_SEC: 40,
  ROOM_DAMAGE_SEC: 45,

  // optional booster rooms (staffed → multiply income)
  LAB_VALUE_MUL: 1.75, // staffed Lab: +75% sale value
  PACK_THROUGHPUT_MUL: 1.65, // staffed Packaging: +65% grow output
  DINER_TRICKLE: 0.18, // passive meals/sec when a diner exists (groceries)
  BASEMENT_HEAT_MUL: 0.5, // basement (underground) production draws half the HEAT

  // economy
  STARTING_CASH: 400,
  FLOOR_COST_BASE: 600, // cost grows per floor
  FLOOR_COST_GROWTH: 1.55,
  SLOTS_PER_FLOOR: 4,
  STARTING_FLOORS: 3,

  // recruit
  RECRUIT_INTERVAL_SEC: 70,
  RECRUIT_OFFER_SEC: 45,

  // events
  EVENT_INTERVAL_SEC: 55,

  // loop
  SIM_STEP_SEC: 0.1, // fixed logic step (pre-speed)
  MAX_CATCHUP_SEC: 1.0,
  AUTOSAVE_MS: 12_000,
} as const;

/** Real time-of-day helpers. */
export function dayOf(t: number): number {
  return Math.floor(t / SIM.DAY_SEC) + 1;
}
export function timeFrac(t: number): number {
  return (t % SIM.DAY_SEC) / SIM.DAY_SEC;
}
export function isNight(t: number): boolean {
  const f = timeFrac(t);
  return f >= SIM.NIGHT_FROM || f < SIM.NIGHT_TO;
}
/** "07:30" style clock string. */
export function clockString(t: number): string {
  const f = timeFrac(t);
  const mins = Math.floor(f * 24 * 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}
